import { useState, useEffect, useCallback } from 'react';
import { EditOutlined, FormOutlined } from '@ant-design/icons';
import { AModal, ASwitch, AInput } from '../../../components';
import { Form } from 'antd';
import { useToast } from '../../../hooks/toast';
import {
  schemaScheduleStatusCreate,
  schemaScheduleStatusEdit,
} from '../schemas/yup.schedule-status.schema';
import { IScheduleStatus } from '../../../shared/interfaces';
import { patchScheduleStatus, postScheduleStatus } from '../../../http';
import { useI18n } from '../../../shared/i18n';

interface IScheduleStatusProps {
  open: boolean;
  close: () => void;
  scheduleStatus: IScheduleStatus | null;
  setSchedulesStatus: React.Dispatch<React.SetStateAction<IScheduleStatus[]>>;
}

const ScheduleStatusCreateEdit: React.FC<IScheduleStatusProps> = ({
  open,
  close,
  scheduleStatus,
  setSchedulesStatus,
}) => {
  const { addToast } = useToast();
  const { t } = useI18n();
  const [description, setDescription] = useState<string>('');
  const [color, setColor] = useState<string>('');
  const [status, setStatus] = useState<boolean>(false);

  const handleCreate = useCallback(async () => {
    schemaScheduleStatusCreate
      .validate({
        description,
        color,
      })
      .then(async (validated) => {
        await postScheduleStatus(validated)
          .then((data) => {
            setSchedulesStatus((state) => [...state, data]);
            addToast({
              type: 'success',
              title: t('schedulesStatusModal.toast.successTitle'),
              description: t('schedulesStatusModal.toast.createdDescription'),
            });
            close();
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: t('schedulesStatusModal.toast.errorTitle'),
              description:
                error.response.data.error ??
                t('schedulesStatusModal.toast.createError'),
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('schedulesStatusModal.toast.blankFieldsTitle'),
          description: err.errors[0],
        });
      });
  }, [description, color, status, t, addToast, close, setSchedulesStatus]);

  const handleEdit = useCallback(async () => {
    schemaScheduleStatusEdit
      .validate({
        description,
        color,
        status,
      })
      .then(async (validated) => {
        await patchScheduleStatus(scheduleStatus?.id, validated)
          .then((data) => {
            setSchedulesStatus((state) =>
              state.map((e) => (e.id !== data.id ? e : data)),
            );
            addToast({
              type: 'info',
              title: t('schedulesStatusModal.toast.infoTitle'),
              description: t('schedulesStatusModal.toast.updatedDescription'),
            });
            close();
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: t('schedulesStatusModal.toast.errorTitle'),
              description:
                error.response.data.error ??
                t('schedulesStatusModal.toast.editError'),
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('schedulesStatusModal.toast.blankFieldsTitle'),
          description: err.errors[0],
        });
      });
  }, [
    description,
    color,
    status,
    t,
    addToast,
    close,
    scheduleStatus,
    setSchedulesStatus,
  ]);

  useEffect(() => {
    if (scheduleStatus) {
      setDescription(scheduleStatus.description);
      setColor(scheduleStatus.color);
      setStatus(scheduleStatus.status);
    }
  }, [open]);

  return (
    <AModal
      open={open}
      close={close}
      title={
        scheduleStatus ? (
          <>
            <EditOutlined /> {t('schedulesStatusModal.titleEdit')}
          </>
        ) : (
          <>
            <FormOutlined /> {t('schedulesStatusModal.titleCreate')}
          </>
        )
      }
      onOk={scheduleStatus ? handleEdit : handleCreate}
    >
      <Form
        layout="vertical"
        initialValues={{ layout: scheduleStatus }}
        style={{ maxWidth: 600 }}
      >
        <Form.Item label={t('schedulesStatusModal.fields.description')}>
          <AInput
            placeholder={t(
              'schedulesStatusModal.fields.descriptionPlaceholder',
            )}
            name="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Form.Item>
        <Form.Item label={t('schedulesStatusModal.fields.color')}>
          <AInput
            type="color"
            placeholder={t('schedulesStatusModal.fields.colorPlaceholder')}
            name="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
          />
        </Form.Item>
        {scheduleStatus && (
          <Form.Item label={t('schedulesStatusModal.fields.active')}>
            <ASwitch
              checked={status}
              checkedChildren={t('common.yes')}
              unCheckedChildren={t('common.no')}
              onChange={() => {
                setStatus(!status);
              }}
            />
          </Form.Item>
        )}
      </Form>
    </AModal>
  );
};

export default ScheduleStatusCreateEdit;
